import React, { useState, useMemo } from "react";
import ReactMarkdown from 'react-markdown';
import toast from "react-hot-toast";
import {
  LayoutDashboard, CheckSquare, GitPullRequest, AlertOctagon, Target,
  ChevronDown, ChevronRight, Check, Clipboard, User, Calendar, Tag,
  AlertTriangle, UserCheck, X, ThumbsUp, ThumbsDown
} from 'lucide-react';

// ─── HELPER COMPONENTS ─────────────────────────────────────────────────────────

// Copy Button Component
const CopyButton = ({ textToCopy }) => {
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(textToCopy).then(() => {
      setIsCopied(true);
      toast.success("Copied to clipboard!");
      setTimeout(() => setIsCopied(false), 2000);
    });
  };

  return (
    <button
      onClick={handleCopy}
      className="p-1.5 rounded-md text-zinc-400 hover:bg-zinc-700 hover:text-zinc-100 transition-colors"
      title="Copy to clipboard"
    >
      {isCopied ? (
        <Check className="h-3.5 w-3.5 text-emerald-400" />
      ) : (
        <Clipboard className="h-3.5 w-3.5" />
      )}
    </button>
  );
};

// Badge Component (Tailwind version)
const Badge = ({ children, colorClass, bgClass }) => (
  <span className={`text-[11px] font-semibold tracking-wide px-2 py-0.5 rounded ${colorClass || 'text-slate-400'} ${bgClass || 'bg-slate-500/10'}`}>
    {children}
  </span>
);

// Impact Dot Component
const ImpactDot = ({ level }) => {
  const colorMap = {
    high: 'bg-red-500',
    medium: 'bg-amber-500',
    low: 'bg-green-500'
  };
  return (
    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${colorMap[level] || 'bg-slate-500'}`} />
  );
};

// Approve/Reject Button Component
const ApproveButton = ({ id, approvedSet, onApprove, onReject }) => {
  const approved = approvedSet.has(id);
  const rejected = approvedSet.has(id + "_rejected");

  if (approved) {
    return (
      <span className="text-xs text-green-400 font-semibold flex items-center gap-1">
        <ThumbsUp className="h-3.5 w-3.5" />
        Approved
      </span>
    );
  }
  if (rejected) {
    return (
      <span className="text-xs text-red-400 font-semibold flex items-center gap-1">
        <ThumbsDown className="h-3.5 w-3.5" />
        Rejected
      </span>
    );
  }
  return (
    <div className="flex gap-1.5">
      <button
        onClick={() => onApprove(id)}
        className="text-xs font-semibold px-3 py-1 rounded border-none bg-green-500/15 text-green-400 cursor-pointer transition-all hover:bg-green-500/25"
      >
        Approve
      </button>
      <button
        onClick={() => onReject(id)}
        className="text-xs font-semibold px-3 py-1 rounded border-none bg-red-500/10 text-red-400 cursor-pointer transition-all hover:bg-red-500/20"
      >
        Reject
      </button>
    </div>
  );
};

// ─── OVERVIEW TAB ──────────────────────────────────────────────────────────────

const OverviewTab = ({ data }) => {
  const stats = useMemo(() => {
    const tasks = data?.task_wise_summary || [];
    const blockers = data?.unique_blockers || [];
    
    const blockedCount = tasks.filter(t => t.is_blocked_in_meeting).length;
    const highImpactBlockers = blockers.filter(b => b.classification?.impact_level === 'high').length;
    const mediumImpactBlockers = blockers.filter(b => b.classification?.impact_level === 'medium').length;
    const lowImpactBlockers = blockers.filter(b => b.classification?.impact_level === 'low').length;
    
    const positiveSentiment = tasks.filter(t => t.sentiment === 'positive').length;
    const negativeSentiment = tasks.filter(t => t.sentiment === 'negative').length;
    const neutralSentiment = tasks.filter(t => t.sentiment === 'neutral').length;
    
    return {
      totalTasks: tasks.length,
      blockedCount,
      highImpactBlockers,
      mediumImpactBlockers,
      lowImpactBlockers,
      positiveSentiment,
      negativeSentiment,
      neutralSentiment
    };
  }, [data]);

  const memberWorkload = useMemo(() => {
    const members = data?.member_wise_updates || [];
    return members.map(m => ({
      name: m.speaker_name || 'Unknown',
      role: m.role || '',
      sentiment: m.sentiment,
      issueCount: m.mentioned_issue_keys?.length || 0
    })).sort((a, b) => b.issueCount - a.issueCount);
  }, [data]);

  const maxIssues = Math.max(...memberWorkload.map(m => m.issueCount), 1);

  return (
    <div className="flex flex-col gap-5">
      {/* Meeting Summary Header */}
      {data?.overall_summary && (
        <div className="bg-gradient-to-br from-blue-500/5 to-violet-500/5 border border-blue-500/15 rounded-xl p-5">
          <div className="flex justify-between items-start mb-3">
            <h4 className="text-sm font-semibold text-zinc-300">Overall Summary</h4>
            <CopyButton textToCopy={data.overall_summary} />
          </div>
          <div className="prose prose-sm prose-invert max-w-none text-zinc-300">
            <ReactMarkdown>{data.overall_summary}</ReactMarkdown>
          </div>
        </div>
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-4">
          <div className="text-3xl font-extrabold text-red-400 leading-none">{stats.highImpactBlockers}</div>
          <div className="text-xs text-zinc-500 mt-1.5 font-medium">High-Impact Blockers</div>
        </div>
        <div className="bg-blue-500/5 border border-blue-500/15 rounded-lg p-4">
          <div className="text-3xl font-extrabold text-blue-400 leading-none">{stats.totalTasks}</div>
          <div className="text-xs text-zinc-500 mt-1.5 font-medium">Total Tasks</div>
        </div>
        <div className="bg-amber-500/5 border border-amber-500/15 rounded-lg p-4">
          <div className="text-3xl font-extrabold text-amber-400 leading-none">{stats.blockedCount}</div>
          <div className="text-xs text-zinc-500 mt-1.5 font-medium">Blocked Tasks</div>
        </div>
        <div className="bg-green-500/5 border border-green-500/15 rounded-lg p-4">
          <div className="text-3xl font-extrabold text-green-400 leading-none">{stats.positiveSentiment}</div>
          <div className="text-xs text-zinc-500 mt-1.5 font-medium">Positive Sentiment</div>
        </div>
      </div>

      {/* Member Workload */}
      {memberWorkload.length > 0 && (
        <div className="bg-zinc-900/40 border border-zinc-700/30 rounded-xl p-5">
          <div className="text-xs font-bold tracking-widest text-zinc-500 uppercase mb-4">
            Member Workload
          </div>
          <div className="flex flex-col gap-2.5">
            {memberWorkload.map((member, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <div className="w-32 text-sm text-zinc-300 flex-shrink-0 font-medium truncate">
                  {member.name}
                </div>
                <div className="flex-1 bg-zinc-700/30 rounded h-1.5 overflow-hidden">
                  <div
                    className="h-full rounded bg-gradient-to-r from-blue-500 to-indigo-500"
                    style={{ width: `${(member.issueCount / maxIssues) * 100}%` }}
                  />
                </div>
                <div className="text-xs text-zinc-500 w-6 text-right">{member.issueCount}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── ACTION ITEMS TAB ──────────────────────────────────────────────────────────

const ActionItemsTab = ({ data, approved, onApprove, onReject }) => {
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState("all");

  const actionItems = data?.extracted_action_items || [];
  
  const owners = useMemo(() => {
    const uniqueOwners = [...new Set(actionItems.map(a => a.owner_email || a.owner_role_or_team || 'Unassigned'))];
    return ["all", ...uniqueOwners];
  }, [actionItems]);

  const filtered = useMemo(() => {
    if (filter === "all") return actionItems;
    return actionItems.filter(a => (a.owner_email || a.owner_role_or_team) === filter);
  }, [actionItems, filter]);

  const approvedCount = actionItems.filter(a => approved.has(a.description + a.owner_email)).length;

  if (actionItems.length === 0) {
    return <div className="text-sm text-zinc-500 text-center py-8">No action items found.</div>;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between flex-wrap gap-2.5">
        <div className="text-xs text-zinc-500">
          <span className="text-green-400 font-bold">{approvedCount}</span> of {actionItems.length} approved for agent execution
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {owners.map(o => (
            <button
              key={o}
              onClick={() => setFilter(o)}
              className={`text-xs px-3 py-1 rounded-full border-none cursor-pointer transition-all ${
                filter === o
                  ? 'bg-blue-500/20 text-blue-300 font-semibold'
                  : 'bg-zinc-700/20 text-zinc-500 font-normal hover:bg-zinc-700/30'
              }`}
            >
              {o === "all" ? "All" : o.split('@')[0]}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        {filtered.map((item, idx) => {
          const itemId = item.description + (item.owner_email || idx);
          const isExpanded = expanded === itemId;
          const isApproved = approved.has(itemId);
          const isRejected = approved.has(itemId + "_rejected");

          return (
            <div
              key={idx}
              className={`rounded-lg overflow-hidden border transition-colors ${
                isApproved
                  ? 'bg-green-500/5 border-green-500/20'
                  : isRejected
                  ? 'bg-red-500/5 border-red-500/15'
                  : 'bg-zinc-900/50 border-zinc-700/20'
              }`}
            >
              <div
                onClick={() => setExpanded(isExpanded ? null : itemId)}
                className="flex items-center gap-3 p-3.5 cursor-pointer"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {item.linked_issue_keys?.length > 0 && (
                      <Badge colorClass="text-blue-400" bgClass="bg-blue-500/10">
                        {item.linked_issue_keys[0]}
                      </Badge>
                    )}
                    <span className="text-sm font-medium text-zinc-300 truncate">{item.description}</span>
                  </div>
                  <div className="flex gap-3 items-center text-xs text-zinc-500">
                    <span>{item.owner_email || item.owner_role_or_team || 'Unassigned'}</span>
                    {item.due_description && (
                      <>
                        <span className="text-zinc-600">·</span>
                        <span className="text-zinc-500">{item.due_description}</span>
                      </>
                    )}
                  </div>
                </div>
                <div onClick={e => e.stopPropagation()}>
                  <ApproveButton
                    id={itemId}
                    approvedSet={approved}
                    onApprove={onApprove}
                    onReject={onReject}
                  />
                </div>
                <ChevronDown
                  className={`h-3.5 w-3.5 text-zinc-500 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                />
              </div>
              {isExpanded && (
                <div className="px-4 pb-4 pt-0 border-t border-zinc-700/10">
                  <div className="pt-3 text-xs text-zinc-500 space-y-1">
                    <div><span className="text-zinc-400 font-medium">Owner:</span> {item.owner_email || item.owner_role_or_team || 'Unassigned'}</div>
                    {item.linked_issue_keys?.length > 0 && (
                      <div><span className="text-zinc-400 font-medium">Linked Issues:</span> {item.linked_issue_keys.join(', ')}</div>
                    )}
                    {item.due_description && (
                      <div><span className="text-zinc-400 font-medium">Due:</span> {item.due_description}</div>
                    )}
                  </div>
                  {isApproved && (
                    <div className="mt-2.5 text-xs text-green-400 italic">
                      ✓ This item is approved and queued for agent execution.
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── JIRA UPDATES TAB ──────────────────────────────────────────────────────────

const JiraUpdatesTab = ({ data, approved, onApprove, onReject }) => {
  const [expanded, setExpanded] = useState(null);

  // Flatten all proposed_jira_issue_updates from all task_wise_summary items
  const jiraUpdates = useMemo(() => {
    const tasks = data?.task_wise_summary || [];
    const updates = [];
    tasks.forEach(task => {
      if (task.proposed_jira_issue_updates?.length > 0) {
        task.proposed_jira_issue_updates.forEach(update => {
          updates.push({
            ...update,
            task_issue_key: task.issue_key
          });
        });
      }
    });
    return updates;
  }, [data]);

  const approvedCount = jiraUpdates.filter(j => approved.has(j.update_id || j.issue_key + j.field_to_update)).length;

  if (jiraUpdates.length === 0) {
    return <div className="text-sm text-zinc-500 text-center py-8">No Jira updates found.</div>;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="text-xs text-zinc-500">
        <span className="text-green-400 font-bold">{approvedCount}</span> of {jiraUpdates.length} Jira updates approved for auto-sync
      </div>

      <div className="flex flex-col gap-2">
        {jiraUpdates.map((item, idx) => {
          const itemId = item.update_id || item.issue_key + item.field_to_update + idx;
          const isExpanded = expanded === itemId;
          const isApproved = approved.has(itemId);

          return (
            <div
              key={itemId}
              className={`rounded-lg overflow-hidden border transition-colors ${
                isApproved
                  ? 'bg-green-500/5 border-green-500/20'
                  : 'bg-zinc-900/50 border-zinc-700/20'
              }`}
            >
              <div
                onClick={() => setExpanded(isExpanded ? null : itemId)}
                className="flex items-center gap-3 p-3.5 cursor-pointer"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <Badge colorClass="text-violet-400" bgClass="bg-violet-500/10">
                      {item.issue_key}
                    </Badge>
                    <span className="text-sm font-medium text-zinc-300">
                      Set <span className="text-cyan-400">{item.field_to_update}</span>
                    </span>
                  </div>
                  <div className="flex gap-2 items-center text-xs text-zinc-500 flex-wrap">
                    <span className="line-through">{item.source_context?.substring(0, 30) || 'Current'}</span>
                    <span>→</span>
                    <Badge colorClass="text-cyan-300" bgClass="bg-cyan-500/10">
                      {item.suggested_value?.substring(0, 30)}
                    </Badge>
                    <span className="text-zinc-600">·</span>
                    <span>{item.owner_email || 'Auto'}</span>
                  </div>
                </div>
                <div onClick={e => e.stopPropagation()}>
                  <ApproveButton
                    id={itemId}
                    approvedSet={approved}
                    onApprove={onApprove}
                    onReject={onReject}
                  />
                </div>
                <ChevronDown
                  className={`h-3.5 w-3.5 text-zinc-500 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                />
              </div>
              {isExpanded && (
                <div className="px-4 pb-4 pt-0 border-t border-zinc-700/10">
                  <div className="pt-3 text-xs text-zinc-500 space-y-1.5">
                    <div><span className="text-zinc-400 font-medium">Issue:</span> {item.issue_key}</div>
                    <div><span className="text-zinc-400 font-medium">Field:</span> {item.field_to_update}</div>
                    <div>
                      <span className="text-zinc-400 font-medium">Change:</span>{' '}
                      <span className="line-through">{item.source_context || 'current'}</span>
                      {' → '}
                      <span className="text-cyan-300">{item.suggested_value}</span>
                    </div>
                    {item.owner_email && (
                      <div><span className="text-zinc-400 font-medium">Owner:</span> {item.owner_email}</div>
                    )}
                    {item.source_context && (
                      <div className="italic text-zinc-500 mt-2">"{item.source_context}"</div>
                    )}
                  </div>
                  {isApproved && (
                    <div className="mt-2.5 text-xs text-green-400 italic">
                      ✓ Approved — agent will update Jira automatically.
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── BLOCKERS TAB ──────────────────────────────────────────────────────────────

const BlockersTab = ({ data }) => {
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState("all");

  const blockers = data?.unique_blockers || [];

  const types = useMemo(() => {
    const uniqueTypes = [...new Set(blockers.map(b => b.classification?.blocker_type).filter(Boolean))];
    return ["all", ...uniqueTypes];
  }, [blockers]);

  const filtered = useMemo(() => {
    if (filter === "all") return blockers;
    return blockers.filter(b => b.classification?.blocker_type === filter);
  }, [blockers, filter]);

  if (blockers.length === 0) {
    return <div className="text-sm text-zinc-500 text-center py-8">No blockers found.</div>;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-1.5 flex-wrap">
        {types.map(t => (
          <button
            key={t}
            onClick={() => setFilter(t)}
            className={`text-xs px-3 py-1 rounded-full border-none cursor-pointer transition-all ${
              filter === t
                ? 'bg-red-500/15 text-red-300 font-semibold'
                : 'bg-zinc-700/20 text-zinc-500 font-normal hover:bg-zinc-700/30'
            }`}
          >
            {t === "all" ? "All" : t}
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-2">
        {filtered.map((item, idx) => {
          const itemId = item.blocker_uuid || idx;
          const isExpanded = expanded === itemId;
          const impactLevel = item.classification?.impact_level || 'medium';

          return (
            <div
              key={itemId}
              className={`rounded-lg overflow-hidden border bg-zinc-900/50 ${
                impactLevel === 'high'
                  ? 'border-red-500/20'
                  : impactLevel === 'medium'
                  ? 'border-amber-500/15'
                  : 'border-green-500/15'
              }`}
              style={{ borderLeftWidth: '3px', borderLeftColor: impactLevel === 'high' ? '#ef4444' : impactLevel === 'medium' ? '#f59e0b' : '#22c55e' }}
            >
              <div
                onClick={() => setExpanded(isExpanded ? null : itemId)}
                className="flex items-center gap-3 p-3.5 cursor-pointer"
              >
                <ImpactDot level={impactLevel} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-zinc-200 mb-1">
                    {item.representative_description}
                  </div>
                  <div className="flex gap-2 items-center flex-wrap">
                    {item.classification?.blocker_type && (
                      <Badge colorClass="text-zinc-400" bgClass="bg-zinc-500/10">
                        {item.classification.blocker_type}
                      </Badge>
                    )}
                    {item.classification?.suggested_owner_role && (
                      <span className="text-xs text-zinc-500">
                        Owner: {item.classification.suggested_owner_role}
                      </span>
                    )}
                    {item.blocking_issue_key && (
                      <span className="text-xs text-red-400">
                        Blocking: {item.blocking_issue_key}
                      </span>
                    )}
                  </div>
                </div>
                <ChevronDown
                  className={`h-3.5 w-3.5 text-zinc-500 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                />
              </div>
              {isExpanded && (
                <div className="px-4 pb-4 pl-10 border-t border-zinc-700/10">
                  <div className="pt-3 text-xs text-zinc-400 space-y-3">
                    {item.blocked_issue_keys?.length > 0 && (
                      <div className="flex gap-2 items-center flex-wrap">
                        <span className="text-zinc-500">Blocked issues:</span>
                        {item.blocked_issue_keys.map(key => (
                          <Badge key={key} colorClass="text-red-400" bgClass="bg-red-500/10">
                            {key}
                          </Badge>
                        ))}
                      </div>
                    )}
                    {item.mentioned_by_emails?.length > 0 && (
                      <div>
                        <span className="text-zinc-500 font-medium">Mentioned by:</span>{' '}
                        {item.mentioned_by_emails.join(', ')}
                      </div>
                    )}
                    {item.all_raw_mentions?.length > 0 && (
                      <div className="space-y-2">
                        <div className="text-zinc-500 font-medium text-xs uppercase tracking-wide">Source Quotes</div>
                        {item.all_raw_mentions.map((quote, i) => (
                          <p key={i} className="text-xs text-zinc-500 italic p-2 bg-zinc-800/50 rounded border-l-2 border-zinc-600">
                            "{quote}"
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── TASK TRACKER TAB ──────────────────────────────────────────────────────────

const TaskTrackerTab = ({ data }) => {
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState("all");

  const tasks = data?.task_wise_summary || [];

  const statuses = useMemo(() => {
    const uniqueStatuses = [...new Set(tasks.map(t => t.jira_details?.status).filter(Boolean))];
    return ["all", ...uniqueStatuses];
  }, [tasks]);

  const filtered = useMemo(() => {
    if (filter === "all") return tasks;
    return tasks.filter(t => t.jira_details?.status === filter);
  }, [tasks, filter]);

  const priorityColors = {
    Highest: 'text-red-400',
    High: 'text-orange-400',
    Medium: 'text-yellow-400',
    Low: 'text-green-400'
  };

  const statusColors = {
    'In Progress': { text: 'text-blue-400', bg: 'bg-blue-500/15' },
    'In Review': { text: 'text-violet-400', bg: 'bg-violet-500/15' },
    'To Do': { text: 'text-slate-400', bg: 'bg-slate-500/10' },
    'Done': { text: 'text-green-400', bg: 'bg-green-500/10' }
  };

  if (tasks.length === 0) {
    return <div className="text-sm text-zinc-500 text-center py-8">No tasks found.</div>;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-1.5 flex-wrap">
        {statuses.map(s => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`text-xs px-3 py-1 rounded-full border-none cursor-pointer transition-all ${
              filter === s
                ? 'bg-indigo-500/15 text-indigo-300 font-semibold'
                : 'bg-zinc-700/20 text-zinc-500 font-normal hover:bg-zinc-700/30'
            }`}
          >
            {s === "all" ? `All (${tasks.length})` : `${s} (${tasks.filter(t => t.jira_details?.status === s).length})`}
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-1.5">
        {filtered.map((item, idx) => {
          const isExpanded = expanded === item.issue_key;
          const statusStyle = statusColors[item.jira_details?.status] || statusColors['To Do'];
          const priorityColor = priorityColors[item.jira_details?.priority] || 'text-zinc-400';

          return (
            <div
              key={item.issue_key || idx}
              className={`rounded-lg overflow-hidden border bg-zinc-900/50 ${
                item.is_blocked_in_meeting
                  ? 'border-orange-500/20'
                  : 'border-zinc-700/20'
              }`}
            >
              <div
                onClick={() => setExpanded(isExpanded ? null : item.issue_key)}
                className="flex items-center gap-3 p-3 cursor-pointer"
              >
                {item.is_blocked_in_meeting && (
                  <ImpactDot level="high" />
                )}
                {!item.is_blocked_in_meeting && (
                  <div className="w-2 h-2" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-bold text-indigo-400 font-mono flex-shrink-0">
                      {item.issue_key}
                    </span>
                    <span className="text-sm text-zinc-300 truncate">
                      {item.jira_details?.summary || item.consolidated_summary || 'No summary'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {item.is_blocked_in_meeting && (
                    <Badge colorClass="text-orange-400" bgClass="bg-orange-500/10">Blocked</Badge>
                  )}
                  <Badge colorClass={statusStyle.text} bgClass={statusStyle.bg}>
                    {item.jira_details?.status || 'Unknown'}
                  </Badge>
                  <span className={`text-xs font-bold ${priorityColor}`}>
                    {item.jira_details?.priority || ''}
                  </span>
                </div>
                <ChevronDown
                  className={`h-3.5 w-3.5 text-zinc-500 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                />
              </div>
              {isExpanded && (
                <div className="px-4 pb-3.5 pl-8 border-t border-zinc-700/10">
                  <div className="pt-2.5 flex gap-4 flex-wrap text-xs text-zinc-500">
                    {item.jira_details?.issuetype && (
                      <div><span className="text-zinc-400 font-medium">Type:</span> {item.jira_details.issuetype}</div>
                    )}
                    {item.jira_details?.assignee_name && (
                      <div><span className="text-zinc-400 font-medium">Assignee:</span> {item.jira_details.assignee_name}</div>
                    )}
                    <div>
                      <span className="text-zinc-400 font-medium">Priority:</span>{' '}
                      <span className={priorityColor}>{item.jira_details?.priority || 'N/A'}</span>
                    </div>
                    {item.is_blocked_in_meeting && (
                      <div className="text-orange-400">⚠ This issue has active blockers</div>
                    )}
                  </div>
                  {item.consolidated_summary && (
                    <div className="mt-3 text-xs text-zinc-400 leading-relaxed">
                      {item.consolidated_summary}
                    </div>
                  )}
                  {item.speaker_contributions?.length > 0 && (
                    <div className="mt-3 space-y-2">
                      <div className="text-zinc-500 font-medium text-xs uppercase tracking-wide">Contributions</div>
                      {item.speaker_contributions.map((c, i) => (
                        <div key={i} className="p-2 bg-zinc-800/50 rounded">
                          <p className="font-semibold text-zinc-300 text-xs">{c.speaker_name}</p>
                          <p className="italic text-zinc-500 text-xs">"{c.quote_or_summary}"</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── MAIN COMPONENT ────────────────────────────────────────────────────────────

export const ProcessedContent = ({ data }) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [approved, setApproved] = useState(new Set());

  const handleApprove = (id) => {
    setApproved(prev => {
      const next = new Set(prev);
      next.delete(id + "_rejected");
      next.add(id);
      return next;
    });
  };

  const handleReject = (id) => {
    setApproved(prev => {
      const next = new Set(prev);
      next.delete(id);
      next.add(id + "_rejected");
      return next;
    });
  };

  // Compute counts for tabs
  const jiraUpdatesCount = useMemo(() => {
    const tasks = data?.task_wise_summary || [];
    let count = 0;
    tasks.forEach(task => {
      if (task.proposed_jira_issue_updates?.length > 0) {
        count += task.proposed_jira_issue_updates.length;
      }
    });
    return count;
  }, [data]);

  const tabs = [
    { id: "overview", label: "Overview", icon: LayoutDashboard },
    { id: "actions", label: "Action Items", icon: CheckSquare, count: data?.extracted_action_items?.length || 0 },
    { id: "jira", label: "Jira Updates", icon: GitPullRequest, count: jiraUpdatesCount },
    { id: "blockers", label: "Blockers", icon: AlertOctagon, count: data?.unique_blockers?.length || 0 },
    { id: "tasks", label: "Tasks", icon: Target, count: data?.task_wise_summary?.length || 0 },
  ];

  if (!data) return null;

  return (
    <div className="flex flex-col">
      {/* Tabs */}
      <div className="flex gap-0.5 bg-zinc-900/80 border border-zinc-700/20 rounded-xl p-1 mb-5">
        {tabs.map(tab => {
          const isActive = activeTab === tab.id;
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg border-none cursor-pointer transition-all text-xs whitespace-nowrap ${
                isActive
                  ? 'bg-blue-500/15 text-blue-300 font-bold'
                  : 'text-zinc-500 font-medium hover:text-zinc-300'
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {tab.label}
              {tab.count !== undefined && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                  isActive
                    ? 'bg-blue-500/25 text-blue-300'
                    : 'bg-zinc-700/30 text-zinc-500'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div>
        {activeTab === "overview" && <OverviewTab data={data} />}
        {activeTab === "actions" && (
          <ActionItemsTab
            data={data}
            approved={approved}
            onApprove={handleApprove}
            onReject={handleReject}
          />
        )}
        {activeTab === "jira" && (
          <JiraUpdatesTab
            data={data}
            approved={approved}
            onApprove={handleApprove}
            onReject={handleReject}
          />
        )}
        {activeTab === "blockers" && <BlockersTab data={data} />}
        {activeTab === "tasks" && <TaskTrackerTab data={data} />}
      </div>
    </div>
  );
};